import { useState, useEffect, useCallback } from 'react'
import { getOfflineTopics } from '_services/audience'
import { schema as eventFeedSchema } from '_services/event-feeds'
import { PSYCHOGRAPHICS_ATTRIBUTES, REMOVED_ATTRIBUTES } from '../constants'

export const usePrebuiltTopics = () => {
  const [offlineAttributes, setOfflineAttributes] = useState([])

  // Fetch data and initialize state
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [offlineResult, eventFeedsResult] = await Promise.all([
          getOfflineTopics(),
          eventFeedSchema('event-feeds'),
        ])

        if (offlineResult && eventFeedsResult) {
          const prebuiltAttributes = []

          // add offline b2b
          Object.keys(offlineResult).forEach((attr) => {
            if (REMOVED_ATTRIBUTES.includes(attr)) {
              return
            }
            prebuiltAttributes.push({
              attrName: attr,
              topics: offlineResult[attr].map((topic) => ({
                topicName: topic?.name || topic,
                attrName: attr,
                atoms: topic.atoms,
                type: attr,
              })),
            })
          })

          // add psychographics
          prebuiltAttributes.push({
            attrName: 'psychographics',
            topics: PSYCHOGRAPHICS_ATTRIBUTES.map((attr) => ({
              atoms: { keywords: [`ls_${attr}`] },
              attrName: 'psychographics',
              topicName: `psychographics_${attr}`,
              type: 'psychographics',
            })),
          })

          // add predictors
          Object.entries(eventFeedsResult?.prebuiltAttributesV2 ?? {}).forEach(([attr, topics]) => {
            if (REMOVED_ATTRIBUTES.includes(attr)) {
              return
            }
            const topicsArray = Object.entries(topics).map(([topicName, { description }]) => ({
              topicName: topicName,
              attrName: attr,
              atoms: description ? { prebuilt: [description] } : [],
              type: attr,
            }))
            if (attr !== 'default' && attr !== 'type')
              prebuiltAttributes.push({ attrName: attr, topics: topicsArray })
          })
          setOfflineAttributes(prebuiltAttributes)
        } else {
          throw new Error('One or both data sources returned null')
        }
      } catch (error) {
        console.error('Error fetching offline attributes:', error)
        toast.error('Error fetching offline attributes.')
      }
    }

    fetchData()
  }, [])

  // Function to apply selected topics
  const getProcessedAttributes = useCallback(
    (selectedTopics) => {
      if (!selectedTopics?.length || !offlineAttributes?.length) {
        return offlineAttributes
      }

      const attributesCopy = JSON.parse(JSON.stringify(offlineAttributes))

      selectedTopics.forEach((topic) => {
        const currentTopic = attributesCopy
          .find(({ attrName }) => attrName === topic.type)
          ?.topics.find(({ topicName }) => topicName === topic.name)

        // ensure topic is one of the prebuilt attributes and not other type of topic
        if (currentTopic) {
          currentTopic.selected = true
          currentTopic.predictorScore = topic.predictorScore
        }
      })

      return attributesCopy
    },
    [offlineAttributes]
  )

  return getProcessedAttributes
}
